import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import ConfirmationModal from "../../../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../../../parts/SvgIcon";
import scriptLoader from "../../../../../../utils/scriptLoader";
import { truncateText } from "../../../../../../utils/string-helpers";
import { orderWidgets } from "../../../../../../utils/widgets";

/**
 * Publish a widget
 * @param {*} e
 * @param {*} id
 */
const publishWidget = (id) => {
  submit(`/actions/widgets/publish/${id}`, {
    method: "GET",
  });
};

/**
 * Unpublish a widget
 * @param {*} e
 * @param {*} id
 */
const unpublishWidget = (id) => {
  submit(`/actions/widgets/unpublish/${id}`, {
    method: "GET",
  });
};

/**
 * Change the status of a widget
 * @param {*} param0
 * @returns
 */
const WidgetToggle = ({ status, id }) => {
  const { t } = useTranslation("coaching-portal");
  const [value, setValue] = useState(status === "draft" ? false : true);
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);

  const handleChange = () => {
    !value ? unpublishWidget(id) : publishWidget(id);
    setTimeout(() => {
      setOpenedConfirmationModal(false);
    }, 500);
  };
  const actionKey = !value ? "unpublishAction" : "publishAction";
  return (
    <>
      <img
        className="toggle-button only-show-mobile"
        src={`/img/new-icons/toggle${value ? "_on" : "_off"}.svg`}
        alt={
          !value ? t("widgets.common.unpublish") : t("widgets.common.publish")
        }
        onClick={() => {
          setValue(!value);
          setOpenedConfirmationModal(!openedConfirmationModal);
        }}
        style={{ position: "relative" }}
      />
      <input
        className="mobile-hidden"
        type="checkbox"
        id={`switch-${id}`}
        name={`switch-${id}`}
        role="switch"
        onChange={() => {
          setValue(!value);
          setOpenedConfirmationModal(!openedConfirmationModal);
        }}
        checked={value ? true : undefined}
      />
      <ConfirmationModal
        title={
          !value
            ? t("widgets.widgetsListManager.unpublish")
            : t("widgets.widgetsListManager.publish")
        }
        contentText={t("widgets.widgetsListManager.confirmPublishUnpublish", {
          action: t("widgets.widgetsListManager." + actionKey),
        })}
        cancelButtonText={t("widgets.widgetsListManager.cancel")}
        cancelButtonAction={() => {
          setOpenedConfirmationModal(false);
          setValue(!value);
        }}
        confirmButtonText={
          !value
            ? t("widgets.widgetsListManager.unpublish")
            : t("widgets.widgetsListManager.publish")
        }
        confirmButtonAction={() => handleChange(id)}
        isOpen={openedConfirmationModal}
      />
    </>
  );
};

export const WidgetsListManager = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const widgets = locals.widgets || locals.widgetsOrganization; //all the widgets

  const [isOrdering, setIsOrdering] = useState(false); //state to control if the widgets in widget manager are in edit mode or change order mode
  const [orderedWidgets, setOrderedWidgets] = useState([]); //array of widget's ids to reorder them
  const [refreshList, setRefreshList] = useState(false);
  /**
   * Reorder the widgets
   */
  useEffect(() => {
    let sortable = null;
    scriptLoader(
      "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
      () => {
        const dragImage = document.createElement("img");
        dragImage.src =
          "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
        sortable = new Sortable(
          document.querySelector(".principal-grabber-div"),
          {
            animation: 350,
            handle: ".drag-wrapper",
            setData(dataTransfer) {
              dataTransfer.setDragImage(dragImage, 0, 0);
            },
            onEnd: (e) => {
              if (e.oldIndex === e.newIndex) return;

              const newOrderedWidgets = Array.from(
                document.querySelectorAll(".item-grabber-container")
              ).map((w) => w.id);

              setOrderedWidgets(newOrderedWidgets);
            },
          }
        );
      }
    );
    return () => {
      if (sortable) sortable.destroy();
    };
  }, []);

  const handleSaveReorder = () => {
    if (orderedWidgets.length > 0) {
      submit("/actions/widgets/reorder", {
        method: "POST",
        body: {
          orderedWidgets,
        },
      });
    }
    setIsOrdering(false);
  };
  const cancelReorder = () => {
    setOrderedWidgets([]);
    setRefreshList(!refreshList);
    setIsOrdering(false);
  };
  return (
    <>
      <section>
        <header>
          <h4>{t("widgets.widgetsListManager.title")}</h4>
          {!isOrdering && (
            <button
              data-button-type="secondary"
              onClick={() => setIsOrdering(true)}
            >
              {t("widgets.widgetsListManager.changeOrder")}
            </button>
          )}
          {isOrdering && (
            <div role="buttons-container">
              <button
                data-button-type="secondary"
                data-action="cancel"
                onClick={cancelReorder}
              >
                {t("widgets.widgetsListManager.cancel")}
              </button>
              <button
                data-button-type="primary"
                onClick={handleSaveReorder}
                disabled={orderedWidgets.length === 0}
              >
                {t("widgets.widgetsListManager.save")}
              </button>
            </div>
          )}
        </header>
        <div className="principal-grabber-div principal-explore">
          {widgets &&
            orderWidgets(widgets).map(
              ({ _id, description, icon, name, status, type }) => (
                <article
                  key={`${_id}-${refreshList}`}
                  id={_id}
                  className="widget-manage-card item-grabber-container"
                >
                  {isOrdering && (
                    <div className="drag-wrapper">
                      <SvgIcon
                        className="drag-handle"
                        src={"/img/new-icons/drag.svg"}
                        noFilterOnLight={true}
                      />
                    </div>
                  )}
                  <div className="body">
                    <SvgIcon src={`/img/widget-icons/${icon}.svg`} />
                    <div>
                      <p className="s2">{truncateText(name, 35)}</p>
                      <p className="p4">{truncateText(description, 20)}</p>
                    </div>
                  </div>
                  <footer>
                    <button
                      data-button-type="text"
                      className="s2 clipped"
                      onClick={() => {
                        submit(`/actions/refresh/?`, {
                          method: "GET",
                          body: {
                            widget: _id,
                            section: "manager",
                            type,
                          },
                        });
                      }}
                    >
                      <span>{t("widgets.widgetsListManager.editWidget")}</span>
                    </button>
                    <WidgetToggle status={status} id={_id} />
                  </footer>
                </article>
              )
            )}
          {/* add new widget */}
          <a
            href={`/actions/refresh?modal[type]=create-widget`}
            className="widget-manage-card add-box"
          >
            <SvgIcon src={"/img/new-icons/add-outline.svg"} />
            <p className="p4">{t("widgets.widgetsListManager.addNewWidget")}</p>
          </a>
        </div>
      </section>
    </>
  );
};
